import type { Vec3 } from '@quake2ts/shared';
import { angleVectors, ZERO_VEC3, normalizeVec3, scaleVec3, clipVelocityVec3 } from '@quake2ts/shared';
import { Entity, Solid } from './entity.js';
import { EntitySystem } from './system.js';

const VEC_UP: Vec3 = { x: 0, y: -1, z: 0 } as const;
const MOVEDIR_UP: Vec3 = { x: 0, y: 0, z: 1 } as const;
const VEC_DOWN: Vec3 = { x: 0, y: -2, z: 0 } as const;
const MOVEDIR_DOWN: Vec3 = { x: 0, y: 0, z: -1 } as const;

function vecEquals(a: Vec3, b: Vec3): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z;
}

export function isZeroVector(vector: Vec3): boolean {
  return vecEquals(vector, ZERO_VEC3 as Vec3);
}

export function setMovedir(angles: Vec3): Vec3 {
  if (vecEquals(angles, VEC_UP)) {
    return { ...MOVEDIR_UP };
  }
  if (vecEquals(angles, VEC_DOWN)) {
    return { ...MOVEDIR_DOWN };
  }
  return { ...angleVectors(angles).forward };
}

export function touchTriggers(ent: Entity, system: EntitySystem): void {
  if (!ent.client) {
    return;
  }

  system.forEachEntity((other) => {
    if (other === ent) return;
    if (other.solid !== Solid.Trigger) return;

    if (
      ent.absmin.x > other.absmax.x ||
      ent.absmax.x < other.absmin.x ||
      ent.absmin.y > other.absmax.y ||
      ent.absmax.y < other.absmin.y ||
      ent.absmin.z > other.absmax.z ||
      ent.absmax.z < other.absmin.z
    ) {
      return;
    }

    if (other.touch) {
      other.touch(other, ent);
    }
  });
}

export function velocityForDamage(damage: number, kick: number, rng: { crandom: () => number }): Vec3 {
  const x = rng.crandom();
  const y = rng.crandom();
  const z = rng.crandom();

  const dir = normalizeVec3({ x, y, z });
  return scaleVec3(dir, damage * kick);
}

export function clipVelocity(inVel: Vec3, normal: Vec3, overbounce: number): Vec3 {
  return clipVelocityVec3(inVel, normal, overbounce);
}

export function G_PickTarget(targetName: string | undefined | null, context: EntitySystem): Entity | null {
  if (!targetName) {
    return null;
  }

  let found: Entity | null = null;
  // TODO: Optimize using a targetname lookup map (e.g. context.find(targetname))
  context.forEachEntity((ent) => {
    if (ent.targetname === targetName) {
      found = ent;
    }
  });
  return found;
}
